import { Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '@/assets/images/logo/logo.svg';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { ChatIcon } from '@/components/ui/ChatIcon';
import { NotificationsScreen } from '@/components/notifications/NotificationsScreen';
import { useNotifications } from '@/shared/hooks/useNotifications';
import { useConversations } from '@/shared/hooks/useConversations';

export const TopNavigation = ({ bgColor, variant }: { bgColor?: string, variant?: string }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const notificationsHook = useNotifications();
  const { unreadCount } = useConversations();

  const isMessagesActive = location.pathname.startsWith('/messages');

  const navClass = 'fixed w-full z-50';
  const iconButtonClass = 'h-9 w-9 flex items-center justify-center rounded-full text-[#8D8D8D] hover:text-white transition-colors';
  const iconClass = 'w-5 h-5';

  return (
    <nav
      className={navClass}
      style={{
        background: 'rgba(40,27,27,0.5)',
        backdropFilter: 'blur(8px)',
        top: 'env(safe-area-inset-top)',
      }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-3 items-center h-16">
          {/* Left: Hamburger */}
          <div className="flex justify-start">
            {user && (
              <button
                data-testid="user-menu"
                className={iconButtonClass}
                onClick={() => navigate('/settings')}
              >
                <Settings className={iconClass} />
              </button>
            )}
          </div>

          {/* Center: Logo */}
          <div className="flex justify-center">
            <img
              src={logo}
              alt="sOSSiety"
              className="h-7 w-auto"
            />
          </div>

          {/* Right: chat icon + notification bell */}
          <div className="flex items-center justify-end gap-1.5">
            {user && (
              <ChatIcon
                unreadCount={unreadCount}
                onClick={() => navigate('/messages')}
                isOpen={isMessagesActive}
                className={iconButtonClass}
                iconClassName={iconClass}
              />
            )}

            {user && (
              <NotificationBell
                unreadCount={notificationsHook.unreadCount}
                openNotifications={notificationsHook.openNotifications}
                isOpen={notificationsHook.isOpen}
                className={iconButtonClass}
                iconClassName={iconClass}
              />
            )}
          </div>
        </div>
      </div>

      {/* Notifications Screen (still a modal — single level of navigation) */}
      <NotificationsScreen
        isOpen={notificationsHook.isOpen}
        onClose={notificationsHook.closeNotifications}
        notifications={notificationsHook.notifications}
        isLoading={notificationsHook.isLoading}
        onNotificationClick={notificationsHook.handleNotificationClick}
        onMarkAllAsRead={notificationsHook.markAllAsRead}
        isMarkingAllAsRead={notificationsHook.isMarkingAllAsRead}
        unreadCount={notificationsHook.unreadCount}
      />
    </nav>
  );
};
